/**
 * Media Downloader
 *
 * Downloads media from WhatsApp messages and saves to local storage.
 * Designed for non-blocking background processing with deduplication cache.
 */

import { downloadMediaMessage } from "@whiskeysockets/baileys";
import fs from "fs/promises";
import path from "path";
import { fileURLToPath } from "url";
import { dirname } from "path";
import { randomUUID } from "crypto";
import pino from "pino";

const logger = pino({ level: "warn" });

// ESM __dirname polyfill
const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// Media storage configuration
const MEDIA_DIR = path.join(__dirname, "../../public/storage/media");
const MEDIA_URL_BASE =
    process.env.MEDIA_URL_BASE ||
    process.env.LARAVEL_APP_URL ||
    "https://scan.flowkirim.com";

// Media size limit configuration (5MB default)
const MAX_MEDIA_SIZE_BYTES =
    parseInt(process.env.MAX_MEDIA_SIZE_MB || "5") * 1024 * 1024;

// Download timeout configuration (30 seconds default)
const DOWNLOAD_TIMEOUT_MS =
    parseInt(process.env.DOWNLOAD_TIMEOUT_SEC || "30") * 1000;

// Maximum concurrent downloads
let MAX_CONCURRENT_DOWNLOADS = 3;

// Track active downloads
let activeDownloads = 0;

// ============================================
// DEDUPLICATION CACHE
// Prevents downloading the same media multiple times
// Key: message_id, Value: { filename, accessibleUrl, size, mimetype, timestamp }
// ============================================
const mediaCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes TTL

// Clean up expired cache entries periodically
setInterval(() => {
    const now = Date.now();
    for (const [key, value] of mediaCache.entries()) {
        if (now - value.timestamp > CACHE_TTL_MS) {
            mediaCache.delete(key);
        }
    }
}, 60 * 1000); // Clean every minute

/**
 * Generate cache key from message
 * Uses message ID if available, otherwise generates a fingerprint
 */
const getCacheKey = (message) => {
    // Try to get message ID
    const mediaTypes = [
        "imageMessage",
        "videoMessage",
        "audioMessage",
        "voiceMessage",
        "documentMessage",
        "stickerMessage",
    ];

    for (const type of mediaTypes) {
        if (message[type]?.url) {
            // Use media URL as unique key (more reliable than message ID)
            return message[type].url;
        }
    }

    return null;
};

// Helper to get file extension from mimetype
const getExtensionFromMime = (mimetype) => {
    const mimeMap = {
        "image/jpeg": ".jpg",
        "image/png": ".png",
        "image/webp": ".webp",
        "image/gif": ".gif",
        "video/mp4": ".mp4",
        "video/webm": ".webm",
        "audio/mpeg": ".mp3",
        "audio/mp4": ".m4a",
        "audio/ogg": ".ogg",
        "audio/amr": ".amr",
        "application/pdf": ".pdf",
        "application/msword": ".doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            ".docx",
        "application/vnd.ms-excel": ".xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            ".xlsx",
        "application/vnd.ms-powerpoint": ".ppt",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            ".pptx",
        "text/plain": ".txt",
        "application/webp": ".webp",
    };
    return mimeMap[mimetype] || ".bin";
};

// Format file size for human readability
const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Ensure media directory exists
 */
export const ensureMediaDir = async () => {
    try {
        await fs.mkdir(MEDIA_DIR, { recursive: true });
        logger.debug(`Media directory ensured: ${MEDIA_DIR}`);
    } catch (error) {
        logger.error({ error: error.message }, `Error creating media directory ${MEDIA_DIR}`);
    }
};

/**
 * Download and save media from message
 * @param {Object} message - Baileys message object
 * @returns {Promise<Object>} Media info { filename, accessibleUrl, size, mimetype }
 */
export const downloadAndSaveMedia = async (message) => {
    let downloadTimeout = null;

    try {
        // SAFETY CHECK 1: Extract media message type first (needed for cache key)
        let mediaMessage = null;
        let mediaType = null;
        const mediaTypes = [
            { type: "imageMessage", name: "image" },
            { type: "videoMessage", name: "video" },
            { type: "audioMessage", name: "audio" },
            { type: "voiceMessage", name: "voice" },
            { type: "documentMessage", name: "document" },
            { type: "stickerMessage", name: "sticker" },
        ];

        for (const { type, name } of mediaTypes) {
            if (message[type]) {
                mediaMessage = message[type];
                mediaType = name;
                break;
            }
        }

        if (!mediaMessage) {
            return null;
        }

        // SAFETY CHECK 2: Check cache for existing download (DEDUPLICATION)
        const cacheKey = getCacheKey(message);
        if (cacheKey && mediaCache.has(cacheKey)) {
            const cached = mediaCache.get(cacheKey);
            logger.warn(
                { cacheKey, filename: cached.filename },
                "Media download CACHE HIT - returning cached result"
            );
            return {
                filename: cached.filename,
                accessibleUrl: cached.accessibleUrl,
                size: cached.size,
                mimetype: cached.mimetype,
                cached: true, // Flag to indicate this came from cache
            };
        }

        // SAFETY CHECK 3: Prevent concurrent download overload
        if (activeDownloads >= MAX_CONCURRENT_DOWNLOADS) {
            logger.warn(
                `Server busy: ${activeDownloads} active downloads, skipping media download`
            );
            return {
                filename: null,
                accessibleUrl: null,
                size: null,
                mimetype: null,
                error: "Server busy: Too many concurrent downloads",
                serverBusy: true,
            };
        }

        // Increment active downloads counter
        activeDownloads++;

        // SAFETY CHECK 3: Pre-check file size from metadata
        const declaredFileLength = mediaMessage.fileLength;
        if (declaredFileLength && declaredFileLength > MAX_MEDIA_SIZE_BYTES) {
            const sizeMB = (declaredFileLength / (1024 * 1024)).toFixed(2);
            const limitMB = (MAX_MEDIA_SIZE_BYTES / (1024 * 1024)).toFixed(2);
            logger.warn(
                `Media exceeds size limit: ${sizeMB}MB > ${limitMB}MB - skipping download`
            );

            const mimetype = mediaMessage.mimetype || "application/octet-stream";

            return {
                filename: null,
                accessibleUrl: null,
                size: declaredFileLength,
                mimetype,
                fileSizeExceeded: true,
                error: `File size (${sizeMB}MB) exceeds limit (${limitMB}MB)`,
            };
        }

        // SAFETY CHECK 4: Download with timeout protection
        let buffer = null;

        const downloadPromise = downloadMediaMessage(
            {
                message,
                retype: () => message,
            },
            "buffer",
            {},
        );

        // Create timeout promise
        const timeoutPromise = new Promise((_, reject) => {
            downloadTimeout = setTimeout(() => {
                reject(new Error(`Download timeout after ${DOWNLOAD_TIMEOUT_MS}ms`));
            }, DOWNLOAD_TIMEOUT_MS);
        });

        // Race between download and timeout
        try {
            buffer = await Promise.race([downloadPromise, timeoutPromise]);
        } catch (timeoutError) {
            if (timeoutError.message.includes('timeout')) {
                logger.error('Media download timeout:', timeoutError.message);
                return {
                    filename: null,
                    accessibleUrl: null,
                    size: null,
                    mimetype: mediaMessage.mimetype || null,
                    error: `Download timeout: ${timeoutError.message}`,
                    timeout: true,
                };
            }
            throw timeoutError;
        } finally {
            if (downloadTimeout) {
                clearTimeout(downloadTimeout);
                downloadTimeout = null;
            }
        }

        if (!buffer || buffer.length === 0) {
            logger.warn("Failed to download media: empty buffer received");
            return null;
        }

        // SAFETY CHECK 5: Verify actual file size
        const fileSize = buffer.length;
        if (fileSize > MAX_MEDIA_SIZE_BYTES) {
            const sizeMB = (fileSize / (1024 * 1024)).toFixed(2);
            const limitMB = (MAX_MEDIA_SIZE_BYTES / (1024 * 1024)).toFixed(2);
            logger.warn(
                `Media file exceeds size limit: ${sizeMB}MB > ${limitMB}MB limit`
            );

            buffer = null;

            const mimetype = mediaMessage.mimetype || "application/octet-stream";

            return {
                filename: null,
                accessibleUrl: null,
                size: fileSize,
                mimetype,
                fileSizeExceeded: true,
                error: `File size (${sizeMB}MB) exceeds limit (${limitMB}MB)`,
            };
        }

        // SAFETY CHECK 6: Validate media directory
        try {
            await fs.access(MEDIA_DIR);
        } catch (dirError) {
            logger.warn(`Media directory not accessible, creating: ${MEDIA_DIR}`);
            await ensureMediaDir();
        }

        // SAFETY CHECK 7: Generate safe filename
        const mimetype = mediaMessage.mimetype || "application/octet-stream";
        const extension = getExtensionFromMime(mimetype);

        // Generate unique filename with UUID
        const filename = `${randomUUID()}${extension}`;

        // Validate filename doesn't contain path traversal
        if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
            throw new Error('Invalid filename detected');
        }

        const filepath = path.join(MEDIA_DIR, filename);

        // SAFETY CHECK 8: Safe file write with cleanup
        try {
            await fs.writeFile(filepath, buffer);
        } catch (writeError) {
            logger.error('Failed to write media file:', writeError);
            buffer = null;
            return {
                filename: null,
                accessibleUrl: null,
                size: fileSize,
                mimetype,
                error: `Failed to save file: ${writeError.message}`,
            };
        }

        // Clean up buffer from memory after successful write
        buffer = null;

        // Generate accessible URL
        const accessibleUrl = `${MEDIA_URL_BASE}/media/${filename}`;

        // Save to cache for deduplication (before returning)
        if (cacheKey) {
            mediaCache.set(cacheKey, {
                filename,
                accessibleUrl,
                size: fileSize,
                mimetype,
                timestamp: Date.now(),
            });
        }

        logger.warn(`Media downloaded and saved: ${filename}`, {
            mediaType,
            mimetype,
            size: fileSize,
            sizeFormatted: formatFileSize(fileSize),
            url: accessibleUrl,
            cached: false,
        });

        return {
            filename,
            accessibleUrl,
            size: fileSize,
            mimetype,
        };

    } catch (error) {
        logger.error({ error: error.message }, `Error downloading media`);

        return {
            filename: null,
            accessibleUrl: null,
            size: null,
            mimetype: null,
            error: `Download failed: ${error.message}`,
        };
    } finally {
        // CRITICAL: Always decrement active downloads
        if (activeDownloads > 0) {
            activeDownloads--;
        }

        // Safety: Clean up any lingering timeout
        if (downloadTimeout) {
            clearTimeout(downloadTimeout);
            downloadTimeout = null;
        }
    }
};

/**
 * Get current active downloads count (for monitoring)
 */
export const getActiveDownloads = () => activeDownloads;

/**
 * Set max concurrent downloads
 */
export const setMaxConcurrentDownloads = (max) => {
    MAX_CONCURRENT_DOWNLOADS = max;
};
